import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import {
  Alert,
  Autocomplete,
  Backdrop,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import {
  Add as AddIcon,
  Business as BusinessIcon,
  EditOutlined as EditIcon,
  LocalOffer as OfferIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import { Tooltip } from "@mui/material";
import api from "../api/api";

const initialCategoryOptions = [{ id: 0, name: "" }];

const pageSize = 4;

const normalizeCategory = (item) => ({
  id: item?.id || item?.Id || item?.categoryId || item?.CategoryId || "",
  name:
    item?.name || item?.Name || item?.categoryName || item?.CategoryName || "",
});

const normalizeBusiness = (item) => ({
  id:
    item?.id || item?.Id || item?.businessId || item?.BusinessId || Date.now(),
  alumniId: item?.alumniId || item?.AlumniId || "",
  alumniName:
    item?.alumniName ||
    item?.AlumniName ||
    `${item?.firstName || item?.FirstName || ""} ${item?.lastName || item?.LastName || ""}`.trim(),
  passedOutYear:
    item?.passedOutYear ||
    item?.PassedOutYear ||
    item?.yearOfPassedOut ||
    item?.YearOfPassedOut ||
    "",
  categoryId: item?.categoryId || item?.CategoryId || "",
  categoryName: item?.categoryName || item?.CategoryName || "",
  businessName: item?.businessName || item?.BusinessName || "",
  createdBy:
    item?.createdBy ||
    item?.CreatedBy ||
    item?.alumniId ||
    item?.AlumniId ||
    "",
  description:
    item?.description ||
    item?.businessDescription ||
    item?.BusinessDescription ||
    "",
  offerForAlumni:
    typeof item?.offerForAlumni === "boolean"
      ? item.offerForAlumni
      : Boolean(item?.OfferForAlumni),
  offerDescription: item?.offerDescription || item?.OfferDescription || "",
  mobileNumber: item?.mobileNumber || item?.MobileNumber || "",
  city: item?.city || item?.City || "",
  state: item?.state || item?.State || "",
  country: item?.country || item?.Country || "",
  createdDate: item?.createdDate || item?.CreatedDate || "",
});

function Entrepreneur() {
  const userState = useSelector((state) => state.user);
  const currentUser = userState?.user || {};
  const currentUserId =
    Number(currentUser.userId || currentUser.id || currentUser.Id) || 9999;
  const currentUserToken =
    currentUser.token || currentUser.accessToken || currentUser.jwtToken || "";
  const [categories, setCategories] = useState(initialCategoryOptions);
  const [businesses, setBusinesses] = useState([]);
  const [businessesLoading, setBusinessesLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [offerFilter, setOfferFilter] = useState("all");
  const [ownershipFilter, setOwnershipFilter] = useState("all");
  const [locationFilter, setLocationFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [editingBusinessId, setEditingBusinessId] = useState(null);
  const [businessDetailLoading, setBusinessDetailLoading] = useState(false);
  const categoriesLoadedRef = useRef(false);
  const [formData, setFormData] = useState({
    categoryName: initialCategoryOptions[0].name,
    businessName: "",
    description: "",
    offerForAlumni: false,
    offerDescription: "",
    mobileNumber: "",
    city: "",
    state: "",
    country: "India",
  });

  useEffect(() => {
    if (!currentUserToken) {
      return;
    }

    let isMounted = true;

    const loadBusinesses = async () => {
      try {
        setBusinessesLoading(true);
        const response = await api.getBusinesses(currentUserToken);
        const apiBusinesses = (
          response?.data?.businesses ||
          response?.data?.data?.businesses ||
          []
        ).map(normalizeBusiness);

        if (!isMounted) {
          return;
        }

        setBusinesses(apiBusinesses);
        setCategories((current) => {
          const businessCategories = apiBusinesses
            .map((item) =>
              item.categoryId && item.categoryName
                ? { id: item.categoryId, name: item.categoryName }
                : null,
            )
            .filter(Boolean);

          const merged = [...current, ...businessCategories].filter(
            (item) => item?.id && item?.name,
          );

          return merged.filter(
            (item, index, array) =>
              array.findIndex(
                (candidate) =>
                  String(candidate.id) === String(item.id) ||
                  candidate.name.toLowerCase() === item.name.toLowerCase(),
              ) === index,
          );
        });
      } catch (error) {
        if (!isMounted) {
          return;
        }

        setBusinesses([]);
      } finally {
        if (isMounted) {
          setBusinessesLoading(false);
        }
      }
    };

    loadBusinesses();

    return () => {
      isMounted = false;
    };
  }, [currentUserToken]);

  useEffect(() => {
    if (!currentUserToken) {
      return;
    }

    const shouldRefresh = dialogOpen || !categoriesLoadedRef.current;

    if (!shouldRefresh) {
      return;
    }

    let isMounted = true;

    const loadCategories = async () => {
      try {
        categoriesLoadedRef.current = true;
        const response = await api.getBusinessCategories(currentUserToken);
        const apiCategories = (
          response?.data?.categories ||
          response?.data?.data?.categories ||
          []
        )
          .map(normalizeCategory)
          .filter((item) => item.id && item.name);

        if (!isMounted || apiCategories.length === 0) {
          return;
        }

        setCategories(apiCategories);
        setFormData((current) => ({
          ...current,
          categoryName: apiCategories.some(
            (item) => item.name === current.categoryName,
          )
            ? current.categoryName
            : apiCategories[0].name,
        }));
      } catch (error) {
        categoriesLoadedRef.current = false;
        // Keep fallback local categories if API fails.
      }
    };

    loadCategories();

    return () => {
      isMounted = false;
    };
  }, [currentUserToken, dialogOpen]);

  const filterCategoryOptions = useMemo(() => {
    const merged = [...categories];

    businesses.forEach((item) => {
      if (item.categoryId && item.categoryName) {
        merged.push({ id: item.categoryId, name: item.categoryName });
      }
    });

    return merged
      .filter((item) => item?.id && item?.name)
      .filter(
        (item, index, array) =>
          array.findIndex(
            (candidate) =>
              String(candidate.id) === String(item.id) ||
              candidate.name.toLowerCase() === item.name.toLowerCase(),
          ) === index,
      )
      .sort((left, right) => left.name.localeCompare(right.name));
  }, [businesses, categories]);

  const filteredBusinesses = useMemo(() => {
    const searchTerm = search.toLowerCase();

    return businesses.filter((item) => {
      const businessName = (item?.businessName || "").toLowerCase();
      const description = (item?.description || "").toLowerCase();
      const alumniName = (item?.alumniName || "").toLowerCase();
      const city = (item?.city || "").toLowerCase();

      const matchesSearch =
        businessName.includes(searchTerm) ||
        description.includes(searchTerm) ||
        alumniName.includes(searchTerm) ||
        city.includes(searchTerm);

      const matchesCategory =
        categoryFilter === "all" || item.categoryId === Number(categoryFilter);
      const matchesOffer =
        offerFilter === "all" ||
        (offerFilter === "with-offer"
          ? item.offerForAlumni
          : !item.offerForAlumni);
      const matchesOwnership =
        ownershipFilter === "all" ||
        (ownershipFilter === "posted-by-me" &&
          Number(item.createdBy || item.alumniId) === currentUserId);
      const matchesLocation =
        locationFilter === "all" || city === locationFilter.toLowerCase();

      return (
        matchesSearch &&
        matchesCategory &&
        matchesOffer &&
        matchesOwnership &&
        matchesLocation
      );
    });
  }, [
    businesses,
    categoryFilter,
    currentUserId,
    locationFilter,
    offerFilter,
    ownershipFilter,
    search,
  ]);

  const totalPages = Math.max(
    1,
    Math.ceil(filteredBusinesses.length / pageSize),
  );
  const pagedBusinesses = filteredBusinesses.slice(
    (page - 1) * pageSize,
    page * pageSize,
  );

  const resetFilters = () => {
    setSearch("");
    setCategoryFilter("all");
    setOfferFilter("all");
    setOwnershipFilter("all");
    setLocationFilter("all");
    setPage(1);
  };

  const resetDialog = () => {
    setDialogOpen(false);
    setEditingBusinessId(null);
    setSubmitError("");
    setFormData({
      categoryName: filterCategoryOptions[0]?.name ?? categories[0]?.name ?? "",
      businessName: "",
      description: "",
      offerForAlumni: false,
      offerDescription: "",
      mobileNumber: "",
      city: "",
      state: "",
      country: "India",
    });
  };

  const handleOpenCreate = () => {
    setEditingBusinessId(null);
    setSubmitError("");
    setFormData({
      categoryName: filterCategoryOptions[0]?.name ?? categories[0]?.name ?? "",
      businessName: "",
      description: "",
      offerForAlumni: false,
      offerDescription: "",
      mobileNumber: "",
      city: "",
      state: "",
      country: "India",
    });
    setDialogOpen(true);
  };

  const handleEditBusiness = async (businessId) => {
    if (!currentUserToken) {
      setSubmitError("Logged-in session token is missing.");
      return;
    }

    try {
      setDialogOpen(true);
      setEditingBusinessId(businessId);
      setSubmitError("");
      setBusinessDetailLoading(true);
      const response = await api.getBusinessById(businessId, currentUserToken);
      const business = normalizeBusiness(
        response?.data?.business ||
          response?.data?.data?.business ||
          response?.data?.data ||
          response?.data,
      );

      setFormData({
        categoryName: business.categoryName || "",
        businessName: business.businessName || "",
        description: business.description || "",
        offerForAlumni: Boolean(business.offerForAlumni),
        offerDescription: business.offerDescription || "",
        mobileNumber: business.mobileNumber || "",
        city: business.city || "",
        state: business.state || "",
        country: business.country || "India",
      });
    } catch (error) {
      setSubmitError(
        error?.response?.data?.message ||
          error?.message ||
          "Unable to load business details.",
      );
    } finally {
      setBusinessDetailLoading(false);
    }
  };

  const handleSubmit = async () => {
    const trimmedCategoryName = formData.categoryName.trim();
    const selectedCategory = filterCategoryOptions.find(
      (item) => item.name.toLowerCase() === trimmedCategoryName.toLowerCase(),
    );

    if (!currentUserToken) {
      setSubmitError("Logged-in session token is missing.");
      return;
    }

    if (!trimmedCategoryName || !formData.businessName.trim()) {
      setSubmitError("Business name and category are required.");
      return;
    }

    const resolvedCategory = selectedCategory || {
      name: trimmedCategoryName,
    };

    if (!selectedCategory) {
      setCategories((current) => [...current, resolvedCategory]);
    }

    const businessPayload = {
      AlumniId: currentUserId,
      CategoryId: resolvedCategory.id || null,
      CategoryName: resolvedCategory.name,
      BusinessName: formData.businessName.trim(),
      BusinessDescription: formData.description.trim(),
      OfferForAlumni: formData.offerForAlumni ? 1 : 0,
      OfferDescription: formData.offerDescription.trim(),
      MobileNumber: formData.mobileNumber.trim(),
      City: formData.city.trim(),
      State: formData.state.trim(),
      Country: formData.country.trim(),
    };

    try {
      setSubmitting(true);
      setSubmitError("");
      const response = editingBusinessId
        ? await api.updateBusiness(
            editingBusinessId,
            businessPayload,
            currentUserToken,
          )
        : await api.createBusiness(businessPayload, currentUserToken);
      const savedBusiness = normalizeBusiness(
        response?.data?.business ||
          response?.data?.data?.business ||
          response?.data?.data ||
          response?.data ||
          { ...businessPayload, BusinessId: editingBusinessId || Date.now() },
      );
      setBusinesses((current) =>
        editingBusinessId
          ? current.map((item) =>
              item.id === editingBusinessId ? savedBusiness : item,
            )
          : [savedBusiness, ...current],
      );
      resetDialog();
      setPage(1);
    } catch (error) {
      setSubmitError(
        error?.response?.data?.message ||
          error?.message ||
          "Unable to save business.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const totalOffers = businesses.filter((item) => item.offerForAlumni).length;
  const cityOptions = Array.from(
    new Set(businesses.map((item) => item.city).filter(Boolean)),
  ).sort();

  return (
    <Box sx={{ bgcolor: "#f8fbff", minHeight: "100vh", py: { xs: 3, md: 5 } }}>
      <Container maxWidth="xl">
        <Paper
          elevation={0}
          sx={{
            borderRadius: 4,
            overflow: "hidden",
            border: "1px solid rgba(15, 23, 42, 0.08)",
            background: "linear-gradient(135deg, #0f172a 0%, #1d4ed8 100%)",
            color: "white",
            mb: 3,
          }}
        >
          <Grid container spacing={0} alignItems="center">
            <Grid item xs={12} md={8} sx={{ p: { xs: 3, md: 5 } }}>
              <Stack spacing={2}>
                <Chip
                  label="Alumni Business Forum"
                  sx={{
                    alignSelf: "flex-start",
                    bgcolor: "rgba(255,255,255,0.16)",
                    color: "white",
                    fontWeight: 700,
                  }}
                />
                <Typography variant="h3" fontWeight={800}>
                  Discover trusted ventures from the Einstein alumni community.
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ maxWidth: 720, color: "rgba(255,255,255,0.9)" }}
                >
                  Browse alumni-owned businesses, filter by category and
                  location, and unlock special offers created exclusively for
                  fellow graduates.
                </Typography>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleOpenCreate}
                    sx={{
                      bgcolor: "white",
                      color: "#0f172a",
                      px: 2.5,
                      py: 1,
                      borderRadius: 999,
                      fontWeight: 700,
                      textTransform: "none",
                      "&:hover": { bgcolor: "#f8fafc" },
                    }}
                  >
                    Add your business
                  </Button>
                  <Button
                    variant="outlined"
                    sx={{
                      color: "white",
                      borderColor: "rgba(255,255,255,0.35)",
                      px: 2.5,
                      py: 1,
                      borderRadius: 999,
                      textTransform: "none",
                      fontWeight: 700,
                    }}
                  >
                    Explore offers
                  </Button>
                </Stack>
              </Stack>
            </Grid>
            <Grid
              item
              xs={12}
              md={4}
              sx={{ p: { xs: 3, md: 5 }, bgcolor: "rgba(255,255,255,0.08)" }}
            >
              <Stack spacing={2}>
                <Box
                  sx={{
                    p: 2.5,
                    bgcolor: "rgba(255,255,255,0.14)",
                    borderRadius: 3,
                  }}
                >
                  <Typography
                    variant="overline"
                    sx={{ color: "rgba(255,255,255,0.72)" }}
                  >
                    Total ventures
                  </Typography>
                  <Typography variant="h4" fontWeight={800}>
                    {businesses.length}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    p: 2.5,
                    bgcolor: "rgba(255,255,255,0.14)",
                    borderRadius: 3,
                  }}
                >
                  <Typography
                    variant="overline"
                    sx={{ color: "rgba(255,255,255,0.72)" }}
                  >
                    Special alumni offers
                  </Typography>
                  <Typography variant="h4" fontWeight={800}>
                    {totalOffers}
                  </Typography>
                </Box>
              </Stack>
            </Grid>
          </Grid>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, md: 3 },
            borderRadius: 3,
            border: "1px solid rgba(15, 23, 42, 0.08)",
            mb: 3,
          }}
        >
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            alignItems={{ xs: "stretch", md: "center" }}
            justifyContent="space-between"
          >
            <TextField
              fullWidth
              placeholder="Search by business, alumni, city or keyword"
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                setPage(1);
              }}
              InputProps={{
                startAdornment: (
                  <SearchIcon sx={{ color: "text.secondary", mr: 1 }} />
                ),
              }}
            />
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
              <FormControl sx={{ minWidth: 180 }} size="small">
                <InputLabel>Category</InputLabel>
                <Select
                  value={categoryFilter}
                  label="Category"
                  onChange={(event) => {
                    setCategoryFilter(event.target.value);
                    setPage(1);
                  }}
                >
                  <MenuItem value="all">All categories</MenuItem>
                  {filterCategoryOptions.map((item) => (
                    <MenuItem key={item.id} value={item.id}>
                      {item.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl sx={{ minWidth: 150 }} size="small">
                <InputLabel>Offer</InputLabel>
                <Select
                  value={offerFilter}
                  label="Offer"
                  onChange={(event) => {
                    setOfferFilter(event.target.value);
                    setPage(1);
                  }}
                >
                  <MenuItem value="all">All businesses</MenuItem>
                  <MenuItem value="with-offer">Offers available</MenuItem>
                  <MenuItem value="without-offer">No offer</MenuItem>
                </Select>
              </FormControl>
              <FormControl sx={{ minWidth: 170 }} size="small">
                <InputLabel>Ownership</InputLabel>
                <Select
                  value={ownershipFilter}
                  label="Ownership"
                  onChange={(event) => {
                    setOwnershipFilter(event.target.value);
                    setPage(1);
                  }}
                >
                  <MenuItem value="all">All businesses</MenuItem>
                  <MenuItem value="posted-by-me">PostedByMe</MenuItem>
                </Select>
              </FormControl>
              <FormControl sx={{ minWidth: 160 }} size="small">
                <InputLabel>City</InputLabel>
                <Select
                  value={locationFilter}
                  label="City"
                  onChange={(event) => {
                    setLocationFilter(event.target.value);
                    setPage(1);
                  }}
                >
                  <MenuItem value="all">All cities</MenuItem>
                  {cityOptions.map((item) => (
                    <MenuItem key={item} value={item}>
                      {item}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
          </Stack>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.5}
            sx={{ mt: 2 }}
          >
            <Button variant="outlined" onClick={resetFilters}>
              Clear filters
            </Button>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ alignSelf: "center" }}
            >
              Showing {filteredBusinesses.length} business
              {filteredBusinesses.length === 1 ? "" : "es"}.
            </Typography>
          </Stack>
        </Paper>

        <TableContainer
          component={Paper}
          elevation={0}
          sx={{ borderRadius: 3, border: "1px solid rgba(15, 23, 42, 0.08)" }}
        >
          {businessesLoading ? (
            <Stack
              alignItems="center"
              justifyContent="center"
              spacing={1.5}
              sx={{ minHeight: 260, p: 4 }}
            >
              <CircularProgress />
              <Typography color="text.secondary">
                Loading businesses...
              </Typography>
            </Stack>
          ) : (
            <Table>
              <TableHead sx={{ bgcolor: "#f8fafc" }}>
                <TableRow>
                  <TableCell>Business</TableCell>
                  <TableCell>Alumni</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Location</TableCell>
                  <TableCell>Offer</TableCell>
                  <TableCell>Contact</TableCell>
                  <TableCell align="right">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pagedBusinesses.map((item) => (
                  <TableRow
                    key={item.id}
                    hover
                    sx={{
                      "& .row-edit-action": {
                        opacity: 0,
                        pointerEvents: "none",
                        transition: "opacity 0.2s ease",
                      },
                      "&:hover .row-edit-action": {
                        opacity: 1,
                        pointerEvents: "auto",
                      },
                    }}
                  >
                    <TableCell>
                      <Box>
                        <Typography fontWeight={700}>
                          {item.businessName}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mt: 0.5 }}
                        >
                          {item.description || "-"}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography fontWeight={600}>
                          {item.alumniName || "-"}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {item.passedOutYear
                            ? `Passed out ${item.passedOutYear}`
                            : "-"}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>{item.categoryName || "-"}</TableCell>
                    <TableCell>
                      {[item.city, item.state].filter(Boolean).join(", ") || "-"}
                    </TableCell>
                    <TableCell>
                      {item.offerForAlumni ? (
                        <Tooltip
                          title={item.offerDescription || "Offer details available"}
                          arrow
                        >
                          <Chip
                            icon={<OfferIcon />}
                            label="Offer available"
                            color="warning"
                            size="small"
                            sx={{ fontWeight: 700 }}
                          />
                        </Tooltip>
                      ) : (
                        <Chip
                          label="Standard listing"
                          size="small"
                          variant="outlined"
                        />
                      )}
                    </TableCell>
                    <TableCell>{item.mobileNumber || "-"}</TableCell>
                    <TableCell align="right">
                      {Number(item.createdBy || item.alumniId) === currentUserId ? (
                        <Tooltip title="Edit business" arrow>
                          <IconButton
                            className="row-edit-action"
                            onClick={() => handleEditBusiness(item.id)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      ) : null}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </TableContainer>

        {!businessesLoading && pagedBusinesses.length === 0 ? (
          <Paper
            elevation={0}
            sx={{
              p: 4,
              mt: 3,
              borderRadius: 3,
              textAlign: "center",
              border: "1px dashed rgba(15, 23, 42, 0.18)",
            }}
          >
            <BusinessIcon
              sx={{ fontSize: 40, color: "text.secondary", mb: 1 }}
            />
            <Typography variant="h6">
              No businesses match the current filters.
            </Typography>
            <Typography color="text.secondary">
              Try another keyword or reset the filters to view the full
              community list.
            </Typography>
          </Paper>
        ) : null}

        <Stack alignItems="center" sx={{ mt: 3 }}>
          <Pagination
            count={totalPages}
            page={page - 1}
            onChange={(_, value) => setPage(value)}
            color="primary"
            shape="rounded"
          />
        </Stack>
      </Container>

      <Dialog
        open={dialogOpen}
        onClose={() => {
          if (!submitting && !businessDetailLoading) {
            resetDialog();
          }
        }}
        maxWidth="md"
        fullWidth
      >
        <Backdrop
          open={submitting || businessDetailLoading}
          sx={{
            position: "absolute",
            zIndex: (theme) => theme.zIndex.modal + 1,
            color: "#fff",
            flexDirection: "column",
            gap: 1.5,
          }}
        >
          <CircularProgress color="inherit" />
          <Typography variant="body2">
            {businessDetailLoading
              ? "Loading business..."
              : editingBusinessId
                ? "Updating business..."
                : "Adding business..."}
          </Typography>
        </Backdrop>
        <DialogTitle>
          {editingBusinessId ? "Edit business" : "Add a new business"}
        </DialogTitle>
        <DialogContent>
          {submitError ? (
            <Alert severity="error" sx={{ mt: 1 }}>
              {submitError}
            </Alert>
          ) : null}
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Business name"
                value={formData.businessName}
                onChange={(event) =>
                  setFormData({ ...formData, businessName: event.target.value })
                }
              />
            </Grid>
            <Grid item xs={12} md={8}>
              <Autocomplete
                freeSolo
                options={filterCategoryOptions.map((item) => item.name)}
                value={formData.categoryName}
                onChange={(_, value) =>
                  setFormData({
                    ...formData,
                    categoryName: typeof value === "string" ? value : "",
                  })
                }
                onInputChange={(_, value) =>
                  setFormData({ ...formData, categoryName: value })
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    label="Category"
                    placeholder="Select or type category"
                    helperText="Select an existing category or type a new one."
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Mobile number"
                value={formData.mobileNumber}
                onChange={(event) =>
                  setFormData({ ...formData, mobileNumber: event.target.value })
                }
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="City"
                value={formData.city}
                onChange={(event) =>
                  setFormData({ ...formData, city: event.target.value })
                }
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="State"
                value={formData.state}
                onChange={(event) =>
                  setFormData({ ...formData, state: event.target.value })
                }
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Country"
                value={formData.country}
                onChange={(event) =>
                  setFormData({ ...formData, country: event.target.value })
                }
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={3}
                label="Description"
                value={formData.description}
                onChange={(event) =>
                  setFormData({ ...formData, description: event.target.value })
                }
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Offer for alumni"
                helperText="Optional. Highlight special deals for the alumni network."
                value={formData.offerDescription}
                onChange={(event) =>
                  setFormData({
                    ...formData,
                    offerDescription: event.target.value,
                  })
                }
              />
            </Grid>
            <Grid item xs={12}>
              <Button
                variant={formData.offerForAlumni ? "contained" : "outlined"}
                color="warning"
                onClick={() =>
                  setFormData({
                    ...formData,
                    offerForAlumni: !formData.offerForAlumni,
                  })
                }
              >
                {formData.offerForAlumni
                  ? "Offer enabled"
                  : "Enable alumni offer"}
              </Button>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => {
              resetDialog();
            }}
            disabled={submitting || businessDetailLoading}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={submitting || businessDetailLoading}
          >
            {submitting
              ? editingBusinessId
                ? "Updating..."
                : "Saving..."
              : editingBusinessId
                ? "Update business"
                : "Save business"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Entrepreneur;
